import type { TemplateFormsState } from '../index';
import { initialItem } from './addNewSection';
import { getRange, shiftCoord, findLastEmptyIndex, shiftCoordUp, findFirstEmptyIndex, generateGridPlaceholders } from 'utils/section/gridUtils';
import type { LayoutType, TemplateSectionItem } from 'types';

interface Payload {
  name: string;
  direction: 'row' | 'col';
  change: 1 | -1;
  layoutType: LayoutType;
  fromStart?: boolean;
  subTabId?: string | null;
}

const isLineOccupied = (items: TemplateSectionItem[], index: number, axis: 'x' | 'y'): boolean => {
  return items.some((item) => {
    if (!item.itemGuid) return false;
    const { start, end } = getRange(item[axis]);
    return index >= start && index <= end;
  });
};

export const setSectionGrid = (state: TemplateFormsState, { payload }: { payload: Payload }) => {
  const { direction, change, name, layoutType, fromStart } = payload;
  const sections = state[layoutType].sections;

  const sectionIndex = sections.findIndex((section) => section.name === name);
  if (sectionIndex === -1) return;

  const sectionForEdit = sections[sectionIndex];

  // Grid resize affects ALL real items in the section (every sub-tab),
  // because sizeX/sizeY is a section-level property.
  const realItems = sectionForEdit.items.filter((item) => !!item.itemGuid);

  let newGridSizeX = sectionForEdit.sizeX;
  let newGridSizeY = sectionForEdit.sizeY;

  const targetAxis = direction === 'row' ? 'y' : 'x';

  let shiftedItems = realItems;

  if (change === 1) {
    if (fromStart) {
      shiftedItems = realItems.map((item) => {
        const currentVal = item[targetAxis];
        const newVal = shiftCoordUp(currentVal, 1);
        return { ...item, [targetAxis]: newVal };
      });
    }

    if (direction === 'row') newGridSizeY += 1;
    else newGridSizeX += 1;
  } else {
    const currentSize = direction === 'row' ? newGridSizeY : newGridSizeX;

    const indexToDelete = fromStart
      ? findFirstEmptyIndex(currentSize, (index) => isLineOccupied(realItems, index, targetAxis))
      : findLastEmptyIndex(currentSize, (index) => isLineOccupied(realItems, index, targetAxis));

    if (indexToDelete === -1) return;

    shiftedItems = realItems.map((item) => {
      const currentVal = item[targetAxis];
      const newVal = shiftCoord(currentVal, indexToDelete);

      if (currentVal === newVal) return item;
      return { ...item, [targetAxis]: newVal };
    });

    if (direction === 'row') newGridSizeY -= 1;
    else newGridSizeX -= 1;
  }

  if (newGridSizeX < 1 || newGridSizeY < 1) return;

  const allItems = generateGridPlaceholders(
    shiftedItems,
    newGridSizeX,
    newGridSizeY,
    initialItem as TemplateSectionItem,
    (sectionForEdit.subTabs ?? []).map((st) => st.id)
  );

  state[layoutType].sections[sectionIndex] = {
    ...sectionForEdit,
    sizeX: newGridSizeX,
    sizeY: newGridSizeY,
    items: allItems,
  };
};
